import { useState, useEffect, useRef } from 'react';
import { X, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { formatRoleLabel } from '../../utils/roles';
import { normalizeRole } from '../../utils/roleNormalization';

const roleLabelMap = {
  org_admin: 'Headquarters Admin',
  org_team: 'Headquarters Team',
  facility_admin: 'Facility Admin',
  facility_manager: 'Facility Manager',
  facility_worker: 'Facility Team Member (Training)',
};

export default function AssignCourseModal({
  course,
  teamMembers,
  onClose,
  audienceLabel = 'team members',
  allowedRoleOptions = ['facility_admin', 'facility_manager', 'facility_worker'],
}) {
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [filterRole, setFilterRole] = useState('all');
  const modalRef = useRef(null);
  const triggerRef = useRef(document.activeElement);

  // Return focus to trigger element when modal closes
  useEffect(() => {
    const trigger = triggerRef.current;
    return () => { trigger?.focus(); };
  }, []);

  // Focus the modal panel on mount
  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  // Close on Escape, trap Tab inside modal
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab' || !modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll(
        'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleToggleMember = (memberId) => {
    if (selectedMembers.includes(memberId)) {
      setSelectedMembers(selectedMembers.filter((id) => id !== memberId));
    } else {
      setSelectedMembers([...selectedMembers, memberId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedMembers.length === filteredMembers.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(filteredMembers.map((m) => m._id));
    }
  };

  const handleAssign = async () => {
    if (selectedMembers.length === 0) {
      toast.error(`Please select at least one ${audienceLabel.replace(/s$/, '')}`);
      return;
    }

    try {
      setLoading(true);
      const assignmentData = {
        courseId: course._id,
        userIds: selectedMembers,
        dueDate: dueDate || null,
      };

      const result = await api.post('/lms/enrollments/assign', assignmentData);
      const enrolledCount = result?.enrolled?.length || 0;
      const errorCount = result?.errors?.length || 0;
      const alreadyCount = result?.alreadyEnrolled?.length || 0;

      if (enrolledCount > 0) {
        toast.success(
          `Course assigned to ${enrolledCount} ${audienceLabel}${alreadyCount ? `, ${alreadyCount} already enrolled` : ''}${errorCount ? `, ${errorCount} failed` : ''}`
        );
      } else if (alreadyCount > 0 && errorCount === 0) {
        toast.error(`Selected ${audienceLabel} were already enrolled`);
        return;
      } else {
        toast.error(result?.errors?.[0]?.error || `No ${audienceLabel} were enrolled`);
        return;
      }
      onClose();
    } catch (error) {
      console.error('Assignment error:', error);
      toast.error(error?.message || 'Failed to assign course');
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getMemberRoles = (member) => {
    if (Array.isArray(member.roles)) return member.roles.map(normalizeRole);
    if (member.role) return [normalizeRole(member.role)];
    return [];
  };

  const filteredMembers = teamMembers.filter((member) => {
    const roles = getMemberRoles(member);
    if (roles.length === 0) return false;
    if (!roles.some((role) => allowedRoleOptions.includes(role))) return false;
    if (filterRole === 'all') return true;
    return roles.includes(filterRole);
  });

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 sm:p-6"
      aria-hidden="true"
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-modal-title"
        tabIndex={-1}
        className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto outline-none"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h2 id="assign-modal-title" className="text-xl sm:text-2xl font-bold text-gray-900">Assign Training</h2>
            <p className="text-sm text-gray-600">{course.title}</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close dialog"
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 py-6">
          {/* Course Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-900">
              <strong>Course:</strong> {course.title}
            </p>
            {course.duration && (
              <p className="text-sm text-blue-900 mt-1">
                <strong>Duration:</strong> {course.duration}
              </p>
            )}
          </div>

          {/* Due Date */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Due Date (Optional)
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              min={getMinDate()}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Leave blank for no due date</p>
          </div>

          {/* Team Members Selection */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-900">
                Select {audienceLabel} ({selectedMembers.length} selected)
              </label>
            </div>

            {/* Filter */}
            <div className="mb-4">
              <label htmlFor="filter-role" className="block text-sm font-medium text-gray-900 mb-1">Filter by role</label>
              <select
                id="filter-role"
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Roles</option>
                {allowedRoleOptions.map((role) => (
                  <option key={role} value={role}>
                    {roleLabelMap[role] || formatRoleLabel(role)}
                  </option>
                ))}
              </select>
            </div>

            {/* Select All */}
            <button
              onClick={handleSelectAll}
              className="mb-4 px-3 py-1 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded border border-primary-200"
            >
              {selectedMembers.length === filteredMembers.length ? 'Deselect All' : 'Select All'}
            </button>

            {/* Desktop List */}
            <div className="hidden sm:block space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
              {filteredMembers.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="mx-auto mb-2 text-gray-400" size={24} aria-hidden="true" />
                  <p className="text-gray-500 text-sm">No {audienceLabel} found</p>
                </div>
              ) : (
                filteredMembers.map((member) => (
                  <label key={member._id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedMembers.includes(member._id)}
                      onChange={() => handleToggleMember(member._id)}
                      className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                    />
                    <div className="flex-grow">
                      <p className="font-medium text-gray-900 text-sm">
                        {member.firstName} {member.lastName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {getMemberRoles(member).map((role) => formatRoleLabel(role)).join(', ')}
                      </p>
                    </div>
                  </label>
                ))
              )}
            </div>

            {/* Mobile List */}
            <div className="sm:hidden space-y-2 max-h-96 overflow-y-auto">
              {filteredMembers.length === 0 ? (
                <div className="bg-gray-50 rounded-lg text-center py-8">
                  <AlertCircle className="mx-auto mb-2 text-gray-400" size={24} aria-hidden="true" />
                  <p className="text-gray-500 text-sm">No {audienceLabel} found</p>
                </div>
              ) : (
                filteredMembers.map((member) => (
                  <label
                    key={member._id}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer active:bg-gray-100"
                  >
                    <input
                      type="checkbox"
                      checked={selectedMembers.includes(member._id)}
                      onChange={() => handleToggleMember(member._id)}
                      className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                    />
                    <div className="flex-grow">
                      <p className="font-medium text-gray-900">
                        {member.firstName} {member.lastName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {getMemberRoles(member).map((role) => formatRoleLabel(role)).join(', ')}
                      </p>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-900 hover:bg-gray-100 rounded-lg font-medium text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={loading || selectedMembers.length === 0}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 font-medium text-sm flex items-center gap-2"
          >
            {loading && <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" aria-hidden="true" />}
            Assign to {selectedMembers.length} Member{selectedMembers.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
